import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { LeftDialog } from 'reactApp/ui/Mobile/LeftDialog/LeftDialog';
import { Logo } from 'reactApp/ui/Mobile/Logo/Logo';
import { UserBlock } from 'reactApp/ui/Mobile/UserBlock/UserBlock';
import { SingleSpace } from 'reactApp/ui/Space/SingleSpace/SingleSpace';
import { Space } from 'reactApp/ui/Space/Space';
import { treeItemsFlatten } from 'reactApp/ui/TreeComponent/TreeComponent.data';
import { TreeNodeData } from 'reactApp/ui/TreeComponent/TreeNode.types';
import { scrollLock, scrollUnlock } from 'reactApp/utils/scrollLock';

import styles from './LeftMenu.css';
import { MenuItem } from './MenuItem/MenuItem';

interface Props {
    onClose: () => void;
}

export const LeftMenu = memo(({ onClose }: Props): ReactElement => {
    const storage = useSelector(getCurrentStorage);
    const { isReactPage } = getStorage(storage);
    const { goToSubscriptions } = useGoToSubscriptions();

    const scrollRef = useRef(null);

    useEffect(() => {
        const el = scrollRef?.current;
        scrollLock(el, { reserveScrollBarGap: false });

        return () => {
            scrollUnlock(el);
        };
    }, []);

    const handleGoToSubscriptions = useCallback(() => {
        // TODO: оставить только historyPush после перехода на реакт всех разделов левого меню.
        if (isReactPage) {
            goToSubscriptions();
        } else {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.location = '/subscriptions';
        }
        onClose();
    }, [goToSubscriptions, onClose]);

    const menuItems = useMemo(() => {
        const items: TreeNodeData[] = Object.values(treeItemsFlatten);
        return items
            .filter((item) => item?.isMobile)
            .map(({ id, title, icon, storage }) => (
                <MenuItem key={id} id={id} title={title} icon={icon} storage={storage} onClose={onClose} />
            ));
    }, [treeItemsFlatten, onClose]);

    return (
        <LeftDialog onClose={onClose}>
            <div className={styles.root}>
                <div className={styles.logo}>
                    <Logo />
                </div>
                <div className={styles.contentWrapper} ref={scrollRef}>
                    <div className={styles.content}>
                        <Space isMobile goToSubscriptions={handleGoToSubscriptions} onClose={onClose} />
                    </div>

                    <div className={styles.menu_items}>{menuItems}</div>
                    <div className={styles.user}>
                        <UserBlock isLeftMenu />
                    </div>
                </div>
            </div>
        </LeftDialog>
    );
});

LeftMenu.displayName = 'LeftMenu';
